import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getStoredWorkspace, storeWorkspace, WORKSPACES } from './workspaceConfig';
/**
 * Source visibility per workspace — which sidebar source nav items
 * are visible in each workspace lens.
 */ const SOURCE_VISIBILITY = {
    command: [
        'files',
        'clins',
        'lcats',
        'pricing',
        'team',
        'history'
    ],
    author: [
        'files',
        'history'
    ],
    pricing: [
        'files',
        'clins',
        'lcats',
        'pricing',
        'team'
    ],
    compliance: [
        'files',
        'history'
    ],
    references: [
        'files',
        'team'
    ]
};
/**
 * useWorkspace — standalone hook that reads/writes workspace state via
 * localStorage + a custom event for cross-component sync.
 *
 * This does NOT use React context because the sidebar and the main
 * content area live in different parts of the component tree (the
 * sidebar renders outside the ProposalDashboardV2 provider). Instead,
 * both call useWorkspace(projectId) independently and stay in sync
 * via a 'workspace-changed' custom event on window.
 *
 * When RBAC ships, this hook will also read the user's role from
 * Cerbos and auto-select the appropriate workspace.
 */ export function useWorkspace(projectId) {
    const pid = projectId ?? '';
    const [workspaceId, setWorkspaceIdState] = useState(()=>getStoredWorkspace(pid));
    // Listen for changes from other components (e.g. sidebar ↔ content).
    useEffect(()=>{
        const handler = (e)=>{
            const detail = e.detail;
            if (detail?.projectId === pid && detail?.workspaceId) {
                setWorkspaceIdState(detail.workspaceId);
            }
        };
        window.addEventListener('workspace-changed', handler);
        return ()=>window.removeEventListener('workspace-changed', handler);
    }, [
        pid
    ]);
    const setWorkspace = useCallback((id)=>{
        setWorkspaceIdState(id);
        storeWorkspace(pid, id);
        // Notify other instances of useWorkspace on the same page.
        window.dispatchEvent(new CustomEvent('workspace-changed', {
            detail: {
                projectId: pid,
                workspaceId: id
            }
        }));
    }, [
        pid
    ]);
    const workspace = WORKSPACES[workspaceId];
    return useMemo(()=>({
            workspace,
            workspaceId,
            setWorkspace,
            showTab: (tab)=>workspace.visibleTabs.includes(tab),
            showSource: (source)=>(SOURCE_VISIBILITY[workspaceId] ?? []).includes(source)
        }), [
        workspace,
        workspaceId,
        setWorkspace
    ]);
}
/**
 * WorkspaceProvider is kept for backwards compat but is now a passthrough.
 * The actual state lives in useWorkspace via localStorage + custom events.
 */ export function WorkspaceProvider({ children }) {
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
}
